<template>
  <!--
    Mark: homescreen tile PNG (black border) — opens confirm sheet.
    No upload/badge overlay (mockup: tile alone).
    Profile: same tile + “Add to Home Screen”.
  -->
  <div
    v-if="variant === 'mark' || visible"
    class="rl-cta"
    :class="[
      variantClass,
      { 'has-affordance': showAffordance },
    ]"
  >
    <button
      v-if="variant === 'mark'"
      type="button"
      class="rl-cta__mark"
      aria-label="Back to Matrix"
      title="Back to Matrix"
      @click="onMarkClick"
    >
      <img
        class="rl-cta__mark-img"
        alt=""
        :src="tileSrc"
        width="59"
        height="59"
      >
    </button>

    <template v-else>
      <button
        type="button"
        class="rl-cta__btn"
        :aria-label="buttonLabel"
        @click="openSheet"
      >
        <img class="rl-cta__btn-mark" alt="" :src="tileSrc" width="28" height="28">
        <span class="rl-cta__label">{{ buttonLabel }}</span>
      </button>
      <p v-if="helperText" class="rl-cta__help">{{ helperText }}</p>
    </template>
  </div>
</template>

<script>
/**
 * Rocket Launcher entry — opens mockup-style Add/Cancel sheet.
 * Tile: /icons/icon-192.png (exact homescreen button art).
 */
import {
  installPath,
  isHomeScreenEnabled,
  isStandalone,
  subscribeInstallState,
} from './homeScreenInstall'
import { openRocketLauncherSheet } from './rocketLauncherSheetBus'
import { HOME_SCREEN_TILE_SRC } from './homeScreenTile'

export default {
  name: 'RocketLauncherButton',
  props: {
    variant: {
      type: String,
      default: 'profile',
      validator: (v) => v === 'mark' || v === 'profile' || v === 'banner',
    },
  },
  data () {
    return {
      tick: 0,
      unsub: null,
      tileSrc: HOME_SCREEN_TILE_SRC,
    }
  },
  computed: {
    variantClass () {
      if (this.variant === 'mark' || this.variant === 'banner') return 'rl-cta--mark'
      return 'rl-cta--profile'
    },
    path () {
      void this.tick
      return installPath()
    },
    showAffordance () {
      void this.tick
      if (!isHomeScreenEnabled()) return false
      if (isStandalone() || this.path === 'installed') return false
      return true
    },
    visible () {
      return this.showAffordance
    },
    buttonLabel () {
      return 'Add to Home Screen'
    },
    markAriaLabel () {
      return 'Back to Matrix'
    },
    helperText () {
      if (this.variant === 'mark' || this.variant === 'banner') return ''
      return 'Opens a short confirm sheet — Add places Accountexec on your Home Screen, or Cancel to dismiss.'
    },
  },
  mounted () {
    this.unsub = subscribeInstallState(() => {
      this.tick += 1
    })
  },
  beforeDestroy () {
    if (typeof this.unsub === 'function') this.unsub()
  },
  methods: {
    openSheet () {
      if (!this.showAffordance) return
      openRocketLauncherSheet()
    },
    onMarkClick () {
      this.$emit('activate')
    },
  },
}
</script>

<style scoped>
.rl-cta--mark {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  line-height: 0;
}
.rl-cta__mark {
  position: relative;
  display: block;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  -webkit-tap-highlight-color: transparent;
}
.rl-cta__mark:focus-visible {
  outline: 2px solid #0089bd;
  outline-offset: 3px;
}
.rl-cta__mark-img {
  display: block;
  /* Banner tile: 66px × 0.9; PNG carries contiguous black stroke */
  width: 59px;
  height: 59px;
  object-fit: contain;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
}

.rl-cta--profile {
  margin-top: 16px;
}
.rl-cta--profile .rl-cta__btn {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
  padding: 10px 16px;
  border: 1.6px solid #12202f;
  border-radius: 12px;
  background: #fff;
  color: #12202f;
  font-family: inherit;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
}
.rl-cta__btn-mark {
  display: block;
  width: 28px;
  height: 28px;
  border-radius: 0;
  flex: 0 0 auto;
  object-fit: contain;
  box-shadow: none;
}
.rl-cta__help {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.4;
  color: #4b5563;
}
</style>
